// Components
import Slider from "@mui/material/Slider";
import { styled } from "@mui/material/styles";

const StyledSlider = styled(Slider, {
  shouldForwardProp: (prop) =>
    prop !== "lastMarkIndex" && prop !== "markLabelColor",
})<{
  markLabelColor: string;
  lastMarkIndex?: number;
  marks?: Array<{ value: number; label: string }>;
}>(({ lastMarkIndex, markLabelColor, marks }) => ({
  color: "#E2772E",
  height: "8px",
  width: "calc(100% - 16px)",
  margin: marks && marks.length > 0 ? "0 8px 8px" : "0",
  padding: "8px 0px",

  "& .MuiSlider-rail": {
    opacity: 1,
    backgroundColor: "#5B6D7A",
  },

  "& .MuiSlider-thumb": {
    width: 16,
    height: 16,
    backgroundColor: "#FFF",
    border: "2px solid #E2772E",
    boxShadow: "none",

    "&:hover, &.Mui-focusVisible": {
      boxShadow: "0px 0px 0px 6px rgba(226, 119, 46, 0.16)",
    },

    "&.Mui-disabled": {
      backgroundColor: "#FFF",
      border: "2px solid #5B6D7A",
    },
  },

  "&.Mui-disabled": {
    color: "#435B6D7A",

    "& .MuiSlider-rail": {
      backgroundColor: "#435B6D7A",
    },
  },

  "& .MuiSlider-mark": {
    backgroundColor: "#FFF",
    width: 0,
    height: 0,
    borderRadius: "50%",
  },

  "& .MuiSlider-markActive": {
    backgroundColor: "#FFF",
  },

  "& .MuiSlider-markLabel": {
    fontSize: "12px",
    color: markLabelColor,
    top: "24px",
  },

  "& .MuiSlider-markLabel[data-index='0']": {
    transform: "translateX(0%)",
  },

  ...(lastMarkIndex !== undefined && {
    [`& .MuiSlider-markLabel[data-index='${lastMarkIndex}']`]: {
      transform: "translateX(-100%)",
    },
  }),
}));

type CustomSliderProps = {
  min: number;
  max: number;
  step: number;
  value: number;
  marks?: Array<{ value: number; label: string }>;
  onChange: (value: number) => void;
  disabled?: boolean;
  markLabelColor?: string;
};

const CustomSlider = ({
  min,
  max,
  step,
  marks,
  value,
  onChange,
  disabled,
  markLabelColor = "#FFF",
}: CustomSliderProps) => {
  // MUI filters marks to min/max before assigning each one's data-index, so the
  // last rendered index isn't necessarily marks.length - 1.
  const visibleMarksCount = marks?.filter(
    (mark) => mark.value >= min && mark.value <= max
  ).length;

  return (
    <StyledSlider
      min={min}
      max={max}
      step={step}
      marks={marks}
      value={value}
      onChange={(_, newValue) => onChange(newValue as number)}
      disabled={disabled}
      lastMarkIndex={visibleMarksCount ? visibleMarksCount - 1 : undefined}
      markLabelColor={markLabelColor}
    />
  );
};

export default CustomSlider;
